// src/components/agent/AgentLayout.js
// Responsive shell for /agent/* — sidebar slides in as a drawer on mobile,
// fixed sidebar on desktop. Mobile-first PWA experience for the team.
import React, { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { auth, db } from '../../firebase';
import Logo from '../Logo';
import IdleTimeout from './IdleTimeout';
import TeamChat from './TeamChat';
import { playChime, showBrowserNotification } from '../../lib/notify';

const NAV_FULL = [
  { to: '/agent/dashboard',  label: 'Overview',       icon: '◈' },
  { to: '/agent/leads',      label: 'Leads',          icon: '☰' },
  { to: '/agent/buyers',     label: 'Buyers',         icon: '◌' },
  { to: '/agent/contracts',  label: 'Contracts',      icon: '✎' },
  { to: '/agent/finances',   label: 'Finances',       icon: '$' },
  { to: '/agent/inbox',      label: 'Inbox',          icon: '✉' },
  { to: '/agent/messages',   label: 'SMS',            icon: '☻' },
  { to: '/agent/reports',    label: 'Reports',        icon: '☷' },
  { to: '/agent/jv-dashboard', label: 'JV Dashboard', icon: '◉' },
  { to: '/agent/knowledge',  label: 'Knowledge Base', icon: '⊕' },
  { to: '/agent/training',   label: 'Training Guide', icon: '★' },
  { to: '/agent/settings',   label: 'Settings',       icon: '⚙' }
];
const NAV_LEAD_SUBMITTER = [
  { to: '/agent/submit',       label: 'Submit a Lead',   icon: '+' },
  { to: '/agent/submissions',  label: 'My Submissions',  icon: '☰' },
  { to: '/agent/knowledge',    label: 'Knowledge Base',  icon: '⊕' },
  { to: '/agent/training',     label: 'Training Guide',  icon: '★' }
];

export default function AgentLayout({ profile, children }) {
  const NAV = profile?.role === 'lead_submitter' ? NAV_LEAD_SUBMITTER : NAV_FULL;
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth < 900 : false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [unreadEmails, setUnreadEmails] = useState(0);
  const [unreadChats, setUnreadChats] = useState(0);
  const [unreadSms, setUnreadSms] = useState(0);
  const [failedSms, setFailedSms] = useState(0);
  const knownSmsLastTs = useRef({}); // leadId → ms of last seen inbound SMS
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem('homeiqa_sidebar_collapsed') === '1'; } catch { return false; }
  });
  const knownEmailIds = useRef(null); // null until first snapshot
  const toggleCollapsed = () => {
    setCollapsed(c => {
      const next = !c;
      try { localStorage.setItem('homeiqa_sidebar_collapsed', next ? '1' : '0'); } catch {}
      return next;
    });
  };

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 900);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Close drawer on route change
  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

  // Listen for the team chat's unread count (broadcast by TeamChat).
  useEffect(() => {
    const handler = (e) => setUnreadChats(e.detail || 0);
    window.addEventListener('homeiqa-chat-unread', handler);
    return () => window.removeEventListener('homeiqa-chat-unread', handler);
  }, []);

  // Watch the lead pipeline for new inbound SMS (replies to texts we sent,
  // voicemails, etc.). Plays chime + browser notification so the agent
  // hears about replies the moment they land — no need to be on the
  // specific lead's page. SMS Cloud Function (openphoneWebhook) also
  // fires an FCM push for lock-screen notifications when the tab is
  // closed entirely.
  useEffect(() => {
    if (!profile?.uid || profile?.role === 'lead_submitter') return;
    let cancelled = false;
    let unsub = () => {};
    (async () => {
      // Resolve pipeline owner (same logic as Dashboard/Leads).
      let pipelineUid = profile.uid;
      if (profile.role === 'agent') {
        pipelineUid = profile.invitedByUid || null;
        if (!pipelineUid) {
          try {
            const aSnap = await getDocs(query(
              collection(db, 'agents'),
              where('role', '==', 'admin'),
              where('status', '==', 'active'),
            ));
            if (!aSnap.empty) pipelineUid = aSnap.docs[0].id;
          } catch {}
        }
        if (!pipelineUid) pipelineUid = profile.uid;
      }
      if (cancelled) return;

      const leadsCol = collection(db, 'agents', pipelineUid, 'leads');
      // Establish baseline: snapshot existing leads' last inbound SMS
      // timestamps so we don't chime for old replies on page load.
      let baselineDone = false;
      try {
        const initial = await getDocs(leadsCol);
        initial.docs.forEach(d => {
          const last = (d.data().smsThread || []).filter(m => m.direction === 'in').slice(-1)[0];
          if (last?.sentAt?.toMillis) knownSmsLastTs.current[d.id] = last.sentAt.toMillis();
          else knownSmsLastTs.current[d.id] = 0;
        });
        baselineDone = true;
      } catch {}

      unsub = onSnapshot(leadsCol, snap => {
        let unread = 0;
        let failed = 0;
        for (const d of snap.docs) {
          const lead = d.data();
          // Shared team model — notify on every lead (no per-agent scope).
          // Flag leads with an UNACKNOWLEDGED outbound delivery failure: a
          // failure newer than the last time someone opened the lead. Opening
          // the lead acks it (clears the count); a later failure re-lights it.
          const failMs = lead.lastSmsFailureAt?.toMillis?.() || 0;
          const ackMs = lead.smsFailuresAckedAt?.toMillis?.() || 0;
          if (failMs > 0 && failMs > ackMs) failed++;
          const inbound = (lead.smsThread || []).filter(m => m.direction === 'in');
          const last = inbound.slice(-1)[0];
          const lastMs = last?.sentAt?.toMillis?.() || 0;
          const prevMs = knownSmsLastTs.current[d.id];

          // Chime when:
          //   - existing lead got a newer inbound than we'd seen, OR
          //   - a brand-new lead arrived (auto-created from inbound
          //     SMS) and it has at least one inbound message
          // Skip during the initial baseline pass.
          const isNewLeadWithInbound = baselineDone && prevMs === undefined && lastMs > 0;
          const isUpdatedLead = baselineDone && prevMs !== undefined && lastMs > prevMs;
          if (isNewLeadWithInbound || isUpdatedLead) {
            playChime('sms');
            const ownerName = (lead.owners || [])[0]?.name || (lead.owners || [])[0]?.phone || 'Seller';
            const addr = lead.address?.line1 || (lead.source === 'inbound_sms' ? 'New lead from SMS' : '(lead)');
            const title = isNewLeadWithInbound
              ? `📨 New lead from SMS · ${ownerName}`
              : `💬 ${ownerName} replied`;
            showBrowserNotification(
              title,
              `${(last.body || '').slice(0, 110)}\n— ${addr}`,
              () => navigate(`/agent/leads/${d.id}`)
            );
          }
          knownSmsLastTs.current[d.id] = lastMs;
          // Unread = inbound messages newer than the lead's lastInboundReadAt
          // (tracked per-agent in the future). For now: count any inbound
          // received in the last 24h that hasn't been opened (proxy).
          const dayAgo = Date.now() - 24 * 60 * 60 * 1000;
          if (lastMs > dayAgo && (!lead.lastInboundReadAt || lead.lastInboundReadAt.toMillis?.() < lastMs)) {
            unread++;
          }
        }
        setUnreadSms(unread);
        setFailedSms(failed);
      }, () => {});
    })();
    return () => { cancelled = true; unsub(); };
  }, [profile?.uid, profile?.role, profile?.invitedByUid, navigate]);

  // Watch the mailbox for unread incoming messages (shared inbox + my personal).
  // Fire chime + browser notification on truly NEW arrivals.
  useEffect(() => {
    if (!profile?.uid || profile?.role === 'lead_submitter') return;

    const onIncoming = (docs) => {
      const all = docs.map(d => ({ id: d.id, ...d.data() }));
      // Visible to me: shared scope, OR addressed to me. Archived
      // messages don't count toward the unread bell.
      const visibleIncoming = all.filter(m =>
        m.direction === 'in'
        && !m.archived
        && (m.scope === 'shared' || m.toUid === profile.uid)
      );
      const unread = visibleIncoming.filter(m => !(m.readBy || []).includes(profile.uid)).length;
      setUnreadEmails(unread);

      // First snapshot establishes the baseline — don't ding for emails
      // that arrived before the user signed in.
      if (knownEmailIds.current === null) {
        knownEmailIds.current = new Set(visibleIncoming.map(m => m.id));
        return;
      }
      const fresh = visibleIncoming.filter(m => !knownEmailIds.current.has(m.id));
      if (fresh.length) {
        playChime('email');
        const last = fresh[fresh.length - 1];
        showBrowserNotification(
          `New email · ${last.fromName || last.from || 'Inbox'}`,
          last.subject || '(no subject)',
          () => navigate('/agent/inbox')
        );
        fresh.forEach(m => knownEmailIds.current.add(m.id));
      }
    };

    const sharedQ = query(collection(db, 'mailbox'), where('scope', '==', 'shared'));
    const mineQ = query(collection(db, 'mailbox'), where('toUid', '==', profile.uid));

    let sharedDocs = [];
    let mineDocs = [];
    const merge = () => {
      const map = new Map();
      [...sharedDocs, ...mineDocs].forEach(d => map.set(d.id, d));
      onIncoming([...map.values()]);
    };
    const u1 = onSnapshot(sharedQ, s => { sharedDocs = s.docs; merge(); }, () => {});
    const u2 = onSnapshot(mineQ, s => { mineDocs = s.docs; merge(); }, () => {});
    return () => { u1(); u2(); };
  }, [profile?.uid, profile?.role, navigate]);

  // Body scroll lock when drawer open
  useEffect(() => {
    document.body.style.overflow = drawerOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [drawerOpen]);

  const handleSignOut = async () => { await signOut(auth); navigate('/agent/login'); };

  // True for the "home" pages — back button is hidden there since there's
  // nothing meaningful to go back to inside the portal.
  const homeRoutes = ['/agent/dashboard', '/agent/leads', '/agent/buyers', '/agent/contracts', '/agent/inbox', '/agent/messages', '/agent/knowledge', '/agent/training', '/agent/settings', '/agent/submit', '/agent/submissions'];
  const isHomeRoute = homeRoutes.includes(location.pathname);

  const goBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate(profile?.role === 'lead_submitter' ? '/agent/submit' : '/agent/dashboard');
  };

  // Collapsed only applies on desktop. Mobile drawer is always full-width.
  const isCollapsed = collapsed && !isMobile;

  const sidebar = (
    <aside style={
      isMobile ? { ...s.sidebar, ...s.sidebarMobile } :
      isCollapsed ? { ...s.sidebar, ...s.sidebarCollapsed } :
      s.sidebar
    }>
      <div style={isCollapsed ? { ...s.sidebarTop, ...s.sidebarTopCollapsed } : s.sidebarTop}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
          <div>
            {isCollapsed ? <Logo size="sm" /> : <Logo />}
            {!isCollapsed && <div style={s.brandSub}>Agent Portal</div>}
          </div>
          {!isMobile && (
            <button
              onClick={toggleCollapsed}
              style={s.collapseBtnTop}
              title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              {isCollapsed ? '›' : '‹'}
            </button>
          )}
        </div>
      </div>
      <nav style={isCollapsed ? { ...s.nav, ...s.navCollapsed } : s.nav}>
        {NAV.map(item => {
          // Inbox row gets a bell + badge that totals unread emails + chats.
          const isInbox = item.to === '/agent/inbox';
          const isSms = item.to === '/agent/messages';
          const inboxUnread = unreadEmails + unreadChats;
          const smsUnread = unreadSms;
          const showBell = (isInbox && inboxUnread > 0) || (isSms && smsUnread > 0);
          const bellCount = isInbox ? inboxUnread : isSms ? smsUnread : 0;
          const showFailed = isSms && failedSms > 0;
          return (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={() => setDrawerOpen(false)}
              title={isCollapsed ? `${item.label}${showBell ? ` · ${bellCount} unread` : ''}` : undefined}
              style={({ isActive }) => ({
                ...s.navItem,
                ...(isCollapsed ? s.navItemCollapsed : {}),
                ...(isActive ? s.navItemActive : {}),
                ...(isActive && isCollapsed ? s.navItemActiveCollapsed : {}),
                position: 'relative'
              })}
            >
              <span style={s.navIcon}>{item.icon}</span>
              {!isCollapsed && (
                <span style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>
                  <span style={{ flex: 1 }}>{item.label}</span>
                  {showBell && (
                    <span style={s.bellWrap} title={`${bellCount} unread`}>
                      <span style={s.bellIcon}>🔔</span>
                      <span style={s.bellCount}>{bellCount > 9 ? '9+' : bellCount}</span>
                    </span>
                  )}
                  {showFailed && (
                    <span style={s.failWrap} title={`${failedSms} lead${failedSms > 1 ? 's' : ''} with a failed text`}>
                      ⚠️ {failedSms > 9 ? '9+' : failedSms}
                    </span>
                  )}
                </span>
              )}
              {isCollapsed && (showBell || showFailed) && (
                <span style={{ ...s.bellDot, ...(showFailed ? { background: '#DC2626', border: `1.5px solid ${BURG_DK}` } : {}) }} aria-label="notifications" />
              )}
            </NavLink>
          );
        })}
      </nav>
      <div style={isCollapsed ? { ...s.sidebarBottom, ...s.sidebarBottomCollapsed } : s.sidebarBottom}>
        {!isCollapsed && (
          <>
            <div style={s.userRow}>
              {profile?.photoURL && (
                <img src={profile.photoURL} alt="" style={s.avatar} referrerPolicy="no-referrer" />
              )}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={s.userName}>{profile?.name || 'Agent'}</div>
                <div style={s.userRole}>
                  {profile?.role === 'admin' ? 'Owner' :
                   profile?.role === 'lead_submitter' ? 'Lead Submitter' : 'Contract Agent'}
                  {profile?.role === 'admin' && <span style={s.adminBadge}>★</span>}
                </div>
              </div>
            </div>
            <button onClick={handleSignOut} style={s.signOut}>Sign out</button>
            <Link to="/" style={s.homeLink}>← Public site</Link>
          </>
        )}
      </div>
    </aside>
  );

  const shellStyle = isMobile
    ? { ...s.shell, ...s.shellMobile }
    : isCollapsed
      ? { ...s.shell, gridTemplateColumns: `${COLLAPSED_W}px 1fr` }
      : s.shell;

  return (
    <div style={shellStyle}>
      {/* Desktop: sidebar always visible. Mobile: drawer + backdrop. */}
      {!isMobile && sidebar}
      {isMobile && drawerOpen && (
        <>
          <div style={s.backdrop} onClick={() => setDrawerOpen(false)} />
          {sidebar}
        </>
      )}

      <div style={s.col}>
        <header style={isMobile ? { ...s.topbar, ...s.topbarMobile } : s.topbar}>
          {isMobile && (
            <button onClick={() => setDrawerOpen(o => !o)} style={s.hamburger} aria-label="Open menu">
              <span style={s.hamburgerLine} />
              <span style={s.hamburgerLine} />
              <span style={s.hamburgerLine} />
            </button>
          )}
          {!isHomeRoute && (
            <button onClick={goBack} style={s.backBtn} aria-label="Go back">
              ← {!isMobile && 'Back'}
            </button>
          )}
          <div style={isMobile ? s.crumbMobile : s.crumb}>
            {isMobile ? <Logo size="sm" dark /> : 'HomeIQa Agent CRM'}
          </div>
          <div style={s.topRight}>
            {!isMobile && <span style={s.userEmail}>{profile?.contactEmail || profile?.email}</span>}
            <button onClick={handleSignOut} style={isMobile ? { ...s.logoutBtn, ...s.logoutBtnMobile } : s.logoutBtn} title="Log out">
              <span style={s.logoutIcon}>⏻</span>{!isMobile && ' Log out'}
            </button>
          </div>
        </header>
        <main style={isMobile ? { ...s.main, ...s.mainMobile } : s.main}>{children}</main>
        {profile?.role !== 'lead_submitter' && <InstallPwaBanner />}
      </div>

      <IdleTimeout />
      <TeamChat profile={profile} />
    </div>
  );
}

// PWA install prompt banner — shows on /agent/* when the browser fires
// beforeinstallprompt and the user hasn't dismissed it.
function InstallPwaBanner() {
  const [prompt, setPrompt] = useState(null);
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem('homeiqa_pwa_dismissed') === '1'; } catch { return false; }
  });

  useEffect(() => {
    const handler = (e) => { e.preventDefault(); setPrompt(e); };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  if (!prompt || dismissed) return null;

  const install = async () => {
    try { prompt.prompt(); await prompt.userChoice; } catch {}
    setPrompt(null);
  };
  const dismiss = () => {
    try { localStorage.setItem('homeiqa_pwa_dismissed', '1'); } catch {}
    setDismissed(true);
  };

  return (
    <div style={pwa.banner}>
      <div style={pwa.body}>
        <strong style={pwa.title}>Install HomeIQa</strong>
        <span style={pwa.desc}>Add the agent portal to your home screen for one-tap access and a native feel.</span>
      </div>
      <div style={pwa.actions}>
        <button onClick={dismiss} style={pwa.dismiss}>Not now</button>
        <button onClick={install} style={pwa.install}>Install</button>
      </div>
    </div>
  );
}

const BURG = '#6B1E2E';
const BURG_DK = '#4A1220';
const GOLD = '#C9A84C';
const GOLD_LITE = '#E2C47A';
const CREAM = '#FAF7F2';
const SIDEBAR_W = 240;
const COLLAPSED_W = 64;

const s = {
  shell: { display: 'grid', gridTemplateColumns: `${SIDEBAR_W}px 1fr`, minHeight: '100vh', fontFamily: "'DM Sans', sans-serif", background: CREAM },
  shellMobile: { display: 'block', gridTemplateColumns: '1fr' },

  sidebar: { background: BURG_DK, color: 'rgba(255,255,255,0.85)', display: 'flex', flexDirection: 'column', position: 'sticky', top: 0, height: '100vh', borderRight: '1px solid rgba(0,0,0,0.1)', transition: 'width 0.2s ease' },
  sidebarCollapsed: { width: COLLAPSED_W, alignItems: 'center' },
  sidebarMobile: { position: 'fixed', top: 0, left: 0, bottom: 0, width: SIDEBAR_W, zIndex: 90, height: '100vh', boxShadow: '4px 0 24px rgba(0,0,0,0.35)', overflowY: 'auto' },
  backdrop: { position: 'fixed', inset: 0, background: 'rgba(28,28,28,0.55)', zIndex: 80, backdropFilter: 'blur(2px)' },

  sidebarTop: { padding: '20px 20px 16px', borderBottom: '1px solid rgba(255,255,255,0.08)' },
  sidebarTopCollapsed: { padding: '16px 0', textAlign: 'center', display: 'flex', justifyContent: 'center' },
  brandSub: { fontSize: 12, fontWeight: 500, color: 'rgba(255,255,255,0.55)', marginTop: 4, fontFamily: "'Inter', sans-serif" },
  nav: { padding: '12px 10px', flex: 1, display: 'flex', flexDirection: 'column', gap: 2 },
  navCollapsed: { padding: '12px 6px' },
  navItem: { display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', borderRadius: 8, fontSize: 14, fontWeight: 500, color: 'rgba(255,255,255,0.75)', textDecoration: 'none', transition: 'background 0.15s ease, color 0.15s ease', fontFamily: "'Inter', sans-serif" },
  navItemCollapsed: { justifyContent: 'center', padding: '10px 4px', gap: 0 },
  navItemActive: { background: 'rgba(201,168,76,0.18)', color: '#FFFFFF' },
  navItemActiveCollapsed: {},
  navIcon: { width: 18, fontSize: 15, opacity: 0.9, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontFamily: "system-ui, -apple-system, sans-serif" },
  bellWrap: { display: 'inline-flex', alignItems: 'center', gap: 4, background: 'rgba(201,168,76,0.15)', border: '1px solid rgba(201,168,76,0.4)', borderRadius: 999, padding: '2px 8px 2px 6px', marginLeft: 'auto' },
  failWrap: { display: 'inline-flex', alignItems: 'center', gap: 3, background: 'rgba(220,38,38,0.18)', border: '1px solid rgba(220,38,38,0.55)', color: '#FECACA', borderRadius: 999, padding: '2px 8px', fontSize: 10, fontWeight: 700, marginLeft: 6 },
  bellIcon: { fontSize: 11, lineHeight: 1 },
  bellCount: { fontSize: 10, color: GOLD_LITE, fontWeight: 600, letterSpacing: '0.02em' },
  bellDot: { position: 'absolute', top: 6, right: 6, width: 8, height: 8, borderRadius: '50%', background: GOLD, border: `1.5px solid ${BURG_DK}` },
  sidebarBottom: { padding: '14px 16px', borderTop: '1px solid rgba(255,255,255,0.08)' },
  sidebarBottomCollapsed: { padding: '10px 6px', borderTop: '1px solid rgba(255,255,255,0.08)' },
  collapseBtnTop: { background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.7)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 6, width: 28, height: 28, padding: 0, fontSize: 18, lineHeight: 1, cursor: 'pointer', fontFamily: "'DM Sans', sans-serif", flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  userRow: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 },
  avatar: { width: 36, height: 36, borderRadius: '50%', border: `1.5px solid ${GOLD}`, flexShrink: 0 },
  userName: { fontSize: 14, fontWeight: 600, color: 'rgba(255,255,255,0.95)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontFamily: "'Inter', sans-serif" },
  userRole: { fontSize: 12, color: 'rgba(255,255,255,0.55)', display: 'flex', alignItems: 'center', gap: 4, fontFamily: "'Inter', sans-serif" },
  adminBadge: { color: GOLD },
  signOut: { width: '100%', background: 'transparent', color: 'rgba(255,255,255,0.85)', border: '1px solid rgba(255,255,255,0.18)', borderRadius: 8, padding: '9px', fontSize: 13, fontWeight: 500, cursor: 'pointer', fontFamily: "'Inter', sans-serif", marginBottom: 8, transition: 'background 0.15s ease' },
  homeLink: { fontSize: 12, color: 'rgba(255,255,255,0.5)', textDecoration: 'none', display: 'block', textAlign: 'center', fontFamily: "'Inter', sans-serif" },

  col: { display: 'flex', flexDirection: 'column', minWidth: 0 },
  topbar: { background: '#fff', borderBottom: `1px solid ${'#E5E7EB'}`, padding: '14px 28px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'sticky', top: 0, zIndex: 50 },
  topbarMobile: { padding: '12px 16px' },
  hamburger: { background: 'transparent', border: 'none', padding: '6px 8px', cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: 4, marginRight: 8 },
  hamburgerLine: { display: 'block', width: 22, height: 2, background: BURG_DK, borderRadius: 2 },
  backBtn: { background: 'transparent', border: '1px solid #E5E7EB', borderRadius: 8, padding: '7px 14px', fontSize: 13, color: '#374151', cursor: 'pointer', fontFamily: "'Inter', sans-serif", marginRight: 12, fontWeight: 500 },
  crumb: { fontFamily: "'Manrope', sans-serif", fontSize: 15, fontWeight: 700, color: '#111827', letterSpacing: '-0.01em' },
  crumbMobile: { display: 'flex', alignItems: 'center' },
  topRight: { display: 'flex', alignItems: 'center', gap: 14 },
  userEmail: { fontSize: 13, color: '#6B7280', fontFamily: "'Inter', sans-serif" },
  logoutBtn: { display: 'inline-flex', alignItems: 'center', gap: 8, background: '#fff', color: '#374151', border: '1px solid #E5E7EB', borderRadius: 8, padding: '8px 14px', fontSize: 13, fontWeight: 500, cursor: 'pointer', fontFamily: "'Inter', sans-serif", transition: 'background 0.15s ease, border-color 0.15s ease' },
  logoutBtnMobile: { padding: '8px 10px', minWidth: 40, justifyContent: 'center' },
  logoutIcon: { fontSize: 14, color: BURG },
  main: { padding: '28px 32px 40px', overflowX: 'hidden' },
  mainMobile: { padding: '18px 14px 80px' }
};

const pwa = {
  banner: { position: 'fixed', bottom: 12, left: 12, right: 12, maxWidth: 480, margin: '0 auto', background: BURG_DK, color: '#fff', borderRadius: 10, padding: '12px 16px', boxShadow: '0 12px 36px rgba(74,18,32,0.4)', border: `1px solid ${GOLD}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, zIndex: 200, fontFamily: "'DM Sans', sans-serif" },
  body: { display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 },
  title: { fontSize: 14, color: GOLD_LITE },
  desc: { fontSize: 11, color: 'rgba(255,255,255,0.7)', lineHeight: 1.5 },
  actions: { display: 'flex', gap: 8, flexShrink: 0 },
  dismiss: { background: 'transparent', border: '1px solid rgba(255,255,255,0.2)', color: 'rgba(255,255,255,0.7)', padding: '6px 12px', borderRadius: 4, fontSize: 11, cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" },
  install: { background: GOLD, color: BURG_DK, border: 'none', padding: '6px 14px', borderRadius: 4, fontSize: 12, fontWeight: 500, cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" }
};
